/**
 *
 * @param congruent
 * @param modulus
 * @returns the remainder of the congruent % modulus operation
 *
 * This function is needed because in some cases the '%' operation gives wrong results.
 * Cause of this misfunctionality is that sometimes numbers are represented in the way like below:
 *      1228.032 % 102.336 = 12 (real result)
 *      1228.032 % 102.336 = 102.33599999999994 (typeScript result)
 *      it is because 1228.032 / 102.336 not represented as 12 but 11.99999 etc.
 *
 * See more: https://stackoverflow.com/questions/37662162/javascript-modulo-behaving-strange
 */

export function modulus(congruent: number, modulus: number): number {
  if (congruent % 1 === 0 && modulus % 1 === 0) {
    return congruent % modulus;
  } else {
    const congruentMultiplicator = getMultiplicatorByFloatingDigits(congruent);
    const modulusMultiplicator = getMultiplicatorByFloatingDigits(modulus);
    const maximumMultiplicator = Math.max(congruentMultiplicator, modulusMultiplicator);
    return divide(
      multiply(congruent, maximumMultiplicator) % multiply(modulus, maximumMultiplicator),
      maximumMultiplicator
    );
  }
}

/**
 * Multiplies two numbers and returns the result
 * It's a workaround for the JS floating point multiplication problem
 */
export function multiply(xNum: number, yNum: number): number {
  const xMultiplicator = getMultiplicatorByFloatingDigits(xNum);
  const yMultiplicator = getMultiplicatorByFloatingDigits(yNum);
  // we can safely use round here because we are sure that the result will be an integer
  const multipliedX = Math.round(xNum * xMultiplicator);
  const multipliedY = Math.round(yNum * yMultiplicator);
  return (multipliedX * multipliedY) / (xMultiplicator * yMultiplicator);
}

/**
 * Divides two numbers and returns the result
 * It's a workaround for the JS floating point division problem
 */
export function divide(xNum: number, yNum: number): number {
  const xMultiplicator = getMultiplicatorByFloatingDigits(xNum);
  const yMultiplicator = getMultiplicatorByFloatingDigits(yNum);
  const maxMultiplicator = Math.max(xMultiplicator, yMultiplicator);
  // we can safely use round here because we are sure that the result will be an integer
  const dividedX = Math.round(xNum * maxMultiplicator);
  const dividedY = Math.round(yNum * maxMultiplicator);
  return dividedX / dividedY;
}

/**
 * Add numbers and returns the result
 * It's a workaround for the JS floating point addition problem
 */
export function add(...numbers: number[]): number {
  let sum = 0;
  let maxMultiplicator = 0;
  numbers.forEach((number) => {
    const currentMultiplicator = getMultiplicatorByFloatingDigits(number);
    if (currentMultiplicator > maxMultiplicator) {
      maxMultiplicator = getMultiplicatorByFloatingDigits(number);
    }
  });
  numbers.forEach((number) => {
    sum += Math.round(number * maxMultiplicator);
  });
  return divide(sum, maxMultiplicator);
}

/**
 * Subtract numbers and returns the result
 * It's a workaround for the JS floating point subtraction problem
 */
export function subtract(xNum: number, yNum: number): number {
  const xMultiplicator = getMultiplicatorByFloatingDigits(xNum);
  const yMultiplicator = getMultiplicatorByFloatingDigits(yNum);
  const maxMultiplicator = Math.max(xMultiplicator, yMultiplicator);

  const x = Math.round(xNum * maxMultiplicator);
  const y = Math.round(yNum * maxMultiplicator);
  const difference = x - y;
  return divide(difference, maxMultiplicator);
}

/**
 * Returns the integer value that is needed to multiply
 * the number `num` to get an integer
 */
function getMultiplicatorByFloatingDigits(num: number) {
  if (num % 1 === 0) {
    return 1;
  }
  return Math.pow(10, num.toString().split('.')[1].length);
}

/**
 * Rounds the number to the specified number of digits
 * @param num - The number to round
 * @param digits - The number of digits to round to
 * @returns The rounded number
 * @example
 * toFixed(1.23456789, 2) // 1.23
 * toFixed(1.23456789, 4) // 1.2346
 */
export function toFixed(num: number, digits: number): number {
  if (digits < 0 || !Number.isInteger(digits)) {
    throw new Error('The number of digits must be a non-negative integer');
  }
  if (num < 0) {
    return -toFixed(-num, digits);
  }
  return +(Math.round(+(num + 'e' + digits)) + 'e' + -digits);
}

/**
 * Pads the number with zeros to the specified number of decimal places.
 * The decimal places not stripped if the 'decimalPlaces' is less than
 * the number of decimal places in the number.
 * @param num - The number to pad
 * @param decimalPlaces - The number of decimal places to pad to
 * @returns String representation of the number with padded decimal places
 * @example
 * padding(1.2, 3) // "1.200"
 * padding(1.234, 2) // "1.234"
 */
export function padding(num: number, decimalPlaces: number): string {
  if (decimalPlaces < 0 || !Number.isInteger(decimalPlaces)) {
    throw new Error('The number of decimal places must be a non-negative integer');
  }
  const numString = num.toString();
  const decimalIndex = numString.indexOf('.');
  if (decimalIndex === -1) {
    return numString + '.' + '0'.repeat(decimalPlaces);
  }
  const decimalPart = numString.slice(decimalIndex + 1);
  const paddingLength = decimalPlaces - decimalPart.length;
  if (paddingLength <= 0) {
    return numString;
  }
  return numString + '0'.repeat(paddingLength);
}
